import { Link } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import {
  ArrowRight,
  BarChart3,
  Check,
  LockKeyhole,
  Newspaper,
  Sparkles,
  Trophy,
} from "lucide-react";
import { getTeamDisplayName, TeamLogo } from "@/components/common/TeamIdentity";

const LADDER_PREVIEW = [
  { rank: 1, team: { id: "nrl-bri", name: "Brisbane Broncos", shortName: "BRI" }, points: 38, move: 0 },
  { rank: 2, team: { id: "nrl-can", name: "Canberra Raiders", shortName: "CAN" }, points: 36, move: 2 },
  { rank: 3, team: { id: "nrl-mel", name: "Melbourne Storm", shortName: "MEL" }, points: 34, move: -1 },
  { rank: 4, team: { id: "nrl-pen", name: "Penrith Panthers", shortName: "PEN" }, points: 32, move: 1 },
  { rank: 5, team: { id: "nrl-cro", name: "Cronulla Sharks", shortName: "CRO" }, points: 30, move: -2 },
];

const PRODUCT_PATHS = [
  {
    kicker: "Explore",
    title: "Predict the run home",
    copy: "Call every remaining game and watch the finals places move in real time. No account, no spreadsheet, no fuss.",
    link: "/ladder/predictor",
    linkLabel: "Open ladder predictor",
    icon: BarChart3,
    accent: "text-ice",
  },
  {
    kicker: "Compete",
    title: "Tip with more context",
    copy: "Make your picks, set a margin, join private competitions and see exactly where every point came from.",
    link: "/login",
    linkLabel: "Start tipping",
    icon: Trophy,
    accent: "text-primary",
  },
  {
    kicker: "Stay across it",
    title: "Read the round",
    copy: "The selection calls, injury notes and form lines that matter — shaped into one useful weekend briefing.",
    link: "/news",
    linkLabel: "Go to the newsroom",
    icon: Newspaper,
    accent: "text-correct",
  },
];

export function HomePage() {
  const { isAuthenticated } = useAuth0();

  return (
    <>
      <section className="relative isolate overflow-hidden border-b border-white/[0.08] bg-navy-900">
        <div className="public-grid absolute inset-0 -z-10 opacity-20" />
        <div className="absolute -right-36 top-10 -z-10 h-96 w-96 rounded-full bg-primary/[0.08] blur-3xl" />

        <div className="mx-auto grid max-w-[1440px] items-center gap-10 px-5 py-10 sm:px-8 sm:py-12 md:grid-cols-[minmax(0,1.05fr)_minmax(300px,0.95fr)] lg:gap-12 lg:px-12 lg:py-16">
          <div className="max-w-3xl">
            <div className="flex items-center gap-2.5 font-data text-[10px] font-bold uppercase tracking-[0.2em] text-ice">
              <span className="h-px w-8 bg-ice" />
              Season toolkit
            </div>
            <h1 className="font-display mt-4 max-w-[800px] text-[clamp(3rem,6vw,5.5rem)] font-black uppercase leading-[0.88] tracking-[-0.03em] text-white">
              The whole season,
              <span className="block text-primary">in front of you.</span>
            </h1>
            <p className="mt-5 max-w-xl text-sm leading-6 text-white/55 sm:text-base sm:leading-7">
              News, tipping and ladder tools for the NRL and AFL season.
            </p>
            <div className="mt-7 flex flex-col gap-3 sm:flex-row">
              <Link
                to="/ladder/predictor"
                className="group inline-flex items-center justify-center gap-3 rounded-md bg-primary px-6 py-3.5 text-sm font-extrabold text-white transition-colors hover:bg-coral-600"
              >
                Build your ladder
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Link>
              <Link
                to={isAuthenticated ? "/tipping/dashboard" : "/login"}
                className="inline-flex items-center justify-center gap-3 rounded-md border border-white/15 bg-white/[0.03] px-6 py-3.5 text-sm font-extrabold text-white transition-colors hover:border-white/30 hover:bg-white/[0.06]"
              >
                {isAuthenticated ? "Go to my tips" : "Join a tipping comp"}
              </Link>
            </div>
            <div className="mt-4 flex flex-wrap gap-x-6 gap-y-2 text-xs font-medium text-white/40">
              <span className="inline-flex items-center gap-2"><Check className="h-3.5 w-3.5 text-correct" /> Predictor is free</span>
              <span className="inline-flex items-center gap-2"><Check className="h-3.5 w-3.5 text-correct" /> No sign-in required</span>
            </div>
          </div>

          <div className="relative mx-auto w-full max-w-[560px] md:mx-0 md:ml-auto">
            <div className="absolute -left-3 top-10 h-[calc(100%-5rem)] w-1 bg-primary sm:-left-5" />
            <div className="overflow-hidden rounded-xl border border-white/[0.12] bg-navy-900/95 shadow-2xl shadow-black/30">
              <div className="flex items-start justify-between border-b border-white/[0.08] px-5 py-5 sm:px-7">
                <div>
                  <p className="font-data text-[10px] uppercase tracking-[0.18em] text-ice">Live predictor</p>
                  <h2 className="font-display mt-1 text-2xl font-extrabold uppercase tracking-wide">Your projected top five</h2>
                </div>
                <span className="rounded-full bg-correct/10 px-2.5 py-1 font-data text-[10px] font-bold uppercase text-correct">18 calls made</span>
              </div>

              <div className="px-3 py-3 sm:px-5">
                {LADDER_PREVIEW.map((team) => (
                  <div key={team.team.id} className="grid grid-cols-[30px_1fr_auto_auto] items-center gap-3 border-b border-white/[0.06] px-2 py-3.5 last:border-0">
                    <span className="font-data text-xs font-bold text-white/40">{String(team.rank).padStart(2, "0")}</span>
                    <div className="flex items-center gap-3">
                      <TeamLogo team={team.team} className="h-9 w-9" />
                      <span className="font-display text-base font-bold tracking-wide">{getTeamDisplayName(team.team)}</span>
                    </div>
                    <span className="font-data text-xs text-white/70">{team.points} PTS</span>
                    <span className={`font-data w-8 text-right text-xs font-bold ${team.move > 0 ? "text-correct" : team.move < 0 ? "text-miss" : "text-white/25"}`}>
                      {team.move > 0 ? `+${team.move}` : team.move || "—"}
                    </span>
                  </div>
                ))}
              </div>

              <div className="flex items-center justify-between bg-white/[0.035] px-5 py-4 sm:px-7">
                <div className="flex items-center gap-2 text-xs text-white/45">
                  <Sparkles className="h-3.5 w-3.5 text-primary" />
                  Updates with every result you pick
                </div>
                <span className="font-data text-[10px] uppercase tracking-widest text-white/30">2026 season</span>
              </div>
            </div>
            <div className="absolute -bottom-5 -right-3 border border-white/10 bg-navy-800 px-4 py-3 shadow-xl sm:-right-5">
              <p className="font-data text-[9px] uppercase tracking-[0.18em] text-white/35">Finals line</p>
              <p className="font-display text-lg font-black uppercase text-white">Finals spots in play</p>
            </div>
          </div>
        </div>
      </section>

      <div className="border-b border-white/[0.08] bg-navy-900">
        <div className="mx-auto flex max-w-[1440px] flex-col gap-3 px-5 py-4 text-sm sm:px-8 md:flex-row md:items-center lg:px-12">
          <span className="shrink-0 font-data text-[10px] font-bold uppercase tracking-[0.2em] text-primary">The weekend read</span>
          <span className="hidden h-4 w-px bg-white/15 md:block" />
          <Link to="/news" className="group flex min-w-0 flex-1 items-center justify-between gap-6 text-white/70 hover:text-white">
            <span className="truncate">The run home, selection watch, form lines and everything that changes your call.</span>
            <ArrowRight className="h-4 w-4 shrink-0 transition-transform group-hover:translate-x-1" />
          </Link>
        </div>
      </div>

      <section className="mx-auto max-w-[1440px] px-5 py-20 sm:px-8 md:py-28 lg:px-12">
        <div className="grid gap-8 border-b border-white/[0.08] pb-12 md:grid-cols-[0.7fr_1.3fr] md:items-end">
          <p className="font-data text-xs font-bold uppercase tracking-[0.2em] text-primary">One platform · three ways in</p>
          <h2 className="font-display max-w-4xl text-4xl font-black uppercase leading-[0.95] tracking-tight sm:text-6xl">
            Follow the season your way.
          </h2>
        </div>

        <div className="grid md:grid-cols-3">
          {PRODUCT_PATHS.map((path, index) => {
            const Icon = path.icon;
            return (
              <article key={path.title} className="group border-b border-white/[0.08] py-9 md:border-b-0 md:border-r md:px-8 md:first:pl-0 md:last:border-r-0 md:last:pr-0">
                <div className="flex items-center justify-between">
                  <Icon className={`h-6 w-6 ${path.accent}`} strokeWidth={1.7} />
                  <span className="font-data text-[10px] text-white/25">0{index + 1}</span>
                </div>
                <p className="mt-8 text-xs font-bold uppercase tracking-[0.16em] text-white/35">{path.kicker}</p>
                <h3 className="font-display mt-2 text-3xl font-extrabold uppercase leading-none">{path.title}</h3>
                <p className="mt-4 max-w-sm text-sm leading-6 text-white/48">{path.copy}</p>
                <Link to={path.link} className="mt-7 inline-flex items-center gap-2 text-sm font-bold text-white transition-colors group-hover:text-primary">
                  {path.linkLabel}
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </Link>
              </article>
            );
          })}
        </div>
      </section>

      <section className="border-y border-white/[0.08] bg-navy-900">
        <div className="mx-auto grid max-w-[1440px] lg:grid-cols-2">
          <div className="px-5 py-16 sm:px-8 md:py-24 lg:border-r lg:border-white/[0.08] lg:px-12">
            <p className="font-data text-xs font-bold uppercase tracking-[0.2em] text-ice">Made for matchday brains</p>
            <h2 className="font-display mt-5 max-w-xl text-4xl font-black uppercase leading-none sm:text-5xl">
              Less hunting. More knowing.
            </h2>
            <p className="mt-5 max-w-xl leading-7 text-white/50">
              Tackle Four brings the useful parts of the season together. Your prediction carries into a live ladder. The news explains what changed. Your tips are ready when you are.
            </p>
          </div>
          <div className="grid gap-px bg-white/[0.08] sm:grid-cols-2">
            {[
              ["Every result", "See the ladder consequence before the siren."],
              ["Both codes", "Switch between NRL and AFL without changing tools."],
              ["Your group", "Private competitions keep the weekend argument alive."],
              ["One account", "Only tipping is gated. The rest stays open."],
            ].map(([title, copy]) => (
              <div key={title} className="bg-navy-900 px-6 py-9 sm:px-8">
                <Check className="h-5 w-5 text-correct" />
                <h3 className="font-display mt-5 text-xl font-extrabold uppercase">{title}</h3>
                <p className="mt-2 text-sm leading-6 text-white/45">{copy}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-[1440px] px-5 py-20 sm:px-8 md:py-28 lg:px-12">
        <div className="relative overflow-hidden border border-white/[0.1] bg-primary px-6 py-12 sm:px-10 md:flex md:items-center md:justify-between md:gap-12 md:py-16">
          <div className="absolute -right-8 -top-14 font-display text-[13rem] font-black leading-none text-white/[0.08]">4</div>
          <div className="relative">
            <p className="font-data text-[10px] font-bold uppercase tracking-[0.2em] text-navy-950/60">Start with the big question</p>
            <h2 className="font-display mt-3 max-w-3xl text-4xl font-black uppercase leading-[0.94] text-white sm:text-6xl">Who makes the eight?</h2>
            <p className="mt-4 max-w-xl text-sm leading-6 text-white/75">Pick the run home and settle it properly. Your ladder updates after every call.</p>
          </div>
          <Link to="/ladder/predictor" className="relative mt-8 inline-flex shrink-0 items-center gap-3 rounded-md bg-navy-950 px-6 py-3.5 text-sm font-extrabold text-white hover:bg-navy-900 md:mt-0">
            Open the predictor
            <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
        <div className="mt-5 flex items-center justify-center gap-2 text-xs text-white/35">
          <LockKeyhole className="h-3.5 w-3.5" />
          Your ladder is saved on this device.
        </div>
      </section>
    </>
  );
}
